import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Marquee from "react-fast-marquee";
import ContactForm from "@/components/ContactForm";
import {
  Globe,
  TrendingUp,
  Search,
  Share2,
  FileText,
  BarChart3,
  ArrowRight,
  CheckCircle,
  Lightbulb,
  Rocket,
  Target,
  Quote,
} from "lucide-react";

const HomePage = () => {
  const services = [
    { icon: Globe, title: "Web Tasarım", desc: "Modern, hızlı ve mobil uyumlu web siteleri" },
    { icon: TrendingUp, title: "Google Ads", desc: "ROI odaklı reklam kampanyaları" },
    { icon: Search, title: "SEO", desc: "Organik trafiğinizi artırın" },
    { icon: Share2, title: "Sosyal Medya", desc: "Marka bilinirliğinizi yükseltin" },
    { icon: FileText, title: "İçerik Pazarlama", desc: "Kaliteli ve özgün içerikler" },
    { icon: BarChart3, title: "Dönüşüm Optimizasyonu", desc: "Daha fazla satış, daha az maliyet" },
  ];

  const steps = [
    { icon: Lightbulb, num: "01", title: "İhtiyaç Analizi", desc: "İşletmenizi ve hedeflerinizi anlıyoruz" },
    { icon: Target, num: "02", title: "Strateji ve Planlama", desc: "Size özel yol haritası oluşturuyoruz" },
    { icon: Rocket, num: "03", title: "Hızlı Aksiyon", desc: "Hızla harekete geçiyor, sonuç alıyoruz" },
  ];

  const testimonials = [
    {
      name: "Ahmet Yılmaz",
      company: "ABC Nakliyat",
      text: "Dijital Yükseliş ile çalışmaya başladıktan sonra müşteri sayımız 3 katına çıktı. Profesyonel ekip ve hızlı iletişim.",
    },
    {
      name: "Fatma Demir",
      company: "Temizlik Plus",
      text: "Google Ads kampanyalarımız artık çok daha verimli. ROI odaklı çalışmaları gerçekten fark yarattı.",
    },
    {
      name: "Mehmet Kaya",
      company: "Kaya Tadilat",
      text: "Web sitemiz yenilendikten sonra telefon çalmaya başladı. 5 iş gününde siteyi yayına aldılar.",
    },
  ];

  const partners = [
    "Google Partner",
    "Meta Partner",
    "Yandex Partner",
    "LinkedIn Partner",
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 sm:py-32" data-testid="hero-section">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-white to-blue-50" />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-30">
          <div className="absolute inset-0 bg-gradient-to-bl from-purple-200 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-purple-600 mb-4">
                ROI BİLİŞİM
              </p>
              <h1
                className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-slate-900 mb-6 font-['Outfit'] leading-tight"
                data-testid="hero-title"
              >
                ROI Odaklı{" "}
                <span className="text-gradient">Dijital Performans</span>{" "}
                Pazarlama Ajansı
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed mb-8 max-w-lg">
                İşinizi <strong>kârlı</strong> bir şekilde büyütüyoruz. Web sitesi ve Google Ads 
                ile doğru müşteriye ulaşın, satışlarınızı artırın.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/iletisim">
                  <Button
                    className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 rounded-full font-semibold text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    data-testid="hero-cta-primary"
                  >
                    Ücretsiz Teklif Al
                    <ArrowRight className="ml-2" size={20} />
                  </Button>
                </Link>
                <Link to="/hizmetler">
                  <Button
                    variant="outline"
                    className="border-2 border-slate-200 hover:border-purple-500 hover:text-purple-600 px-8 py-6 rounded-full font-semibold text-lg transition-all duration-300"
                    data-testid="hero-cta-secondary"
                  >
                    Hizmetlerimiz
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 60 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative hidden lg:block"
            >
              <img
                src="https://static.prod-images.emergentagent.com/jobs/32fb14c0-a8c3-489f-8dd5-d03a17738342/images/6ebbcd9d656f55004a5ed618682129bcbf8d066922345be24aad57bd074c44ac.png"
                alt="Digital Marketing"
                className="w-full max-w-lg mx-auto"
                data-testid="hero-image"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partners Marquee */}
      <section className="py-8 bg-slate-50 border-y border-slate-100" data-testid="partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-4">
          <p className="text-center text-sm text-slate-500 font-medium">
            Resmi Partnerliklerimiz
          </p>
        </div>
        <Marquee gradient={false} speed={40} pauseOnHover>
          <div className="flex items-center gap-16 px-8">
            {[...partners, ...partners].map((partner, index) => (
              <div
                key={index}
                className="flex items-center gap-2 text-slate-400 hover:text-purple-600 transition-colors"
              >
                <CheckCircle size={20} />
                <span className="text-lg font-semibold whitespace-nowrap">{partner}</span>
              </div>
            ))}
          </div>
        </Marquee>
      </section>

      {/* How We Work */}
      <section className="py-24 sm:py-32" data-testid="how-we-work-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-purple-600 mb-4">
              NASIL ÇALIŞIYORUZ
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              İşinizi büyüten <span className="text-gradient">dinamik bir ajans</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="relative p-8 rounded-3xl bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all group"
                data-testid={`step-${index + 1}`}
              >
                <span className="absolute top-6 right-6 text-6xl font-black text-slate-100 font-['Outfit']">
                  {step.num}
                </span>
                <div className="p-3 rounded-xl bg-purple-50 text-purple-600 inline-block mb-4 group-hover:bg-purple-100 transition-colors">
                  <step.icon size={28} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 font-['Outfit']">
                  {step.title}
                </h3>
                <p className="text-slate-600">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-24 sm:py-32 bg-slate-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-purple-600 mb-4">
              HİZMETLERİMİZ
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              Dijitalde işinizi <span className="text-gradient">büyütecek</span> çözümler
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative p-8 rounded-3xl bg-white border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all group gradient-border-hover overflow-hidden"
                data-testid={`service-card-${index + 1}`}
              >
                <div className="p-3 rounded-xl bg-purple-50 text-purple-600 inline-block mb-4 group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-blue-600 group-hover:text-white transition-all">
                  <service.icon size={28} />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2 font-['Outfit']">
                  {service.title}
                </h3>
                <p className="text-slate-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/hizmetler">
              <Button
                variant="outline"
                className="border-2 border-purple-200 hover:border-purple-500 text-purple-600 px-8 py-6 rounded-full font-semibold text-lg transition-all"
              >
                Tüm Hizmetleri Gör
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 sm:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-purple-600 mb-4">
              MÜŞTERİ YORUMLARI
            </p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 font-['Outfit']">
              Müşterilerimizin <span className="text-gradient">deneyimleri</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="p-8 rounded-3xl bg-slate-50 border border-slate-100"
                data-testid={`testimonial-${index + 1}`}
              >
                <Quote className="text-purple-300 mb-4" size={32} />
                <p className="text-slate-600 mb-6 leading-relaxed">{testimonial.text}</p>
                <div>
                  <p className="font-semibold text-slate-900">{testimonial.name}</p>
                  <p className="text-sm text-purple-600">{testimonial.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-gradient-to-r from-purple-600 via-pink-500 to-blue-600" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit'] mb-6">
              İşinizi dijitalde büyütmeye hazır mısınız?
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
              Ücretsiz analiz için hemen iletişime geçin. Size özel dijital pazarlama stratejinizi birlikte oluşturalım.
            </p>
            <Link to="/iletisim">
              <Button
                className="bg-white text-purple-600 hover:bg-slate-100 px-10 py-6 rounded-full font-semibold text-lg shadow-xl hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
              >
                Ücretsiz Teklif Al
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 sm:py-32 bg-white" data-testid="contact-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 font-['Outfit'] mb-4">
              Hemen <span className="text-gradient">İletişime Geçin</span>
            </h2>
            <p className="text-slate-600">
              Size yardımcı olmaktan mutluluk duyarız
            </p>
          </motion.div>
          <ContactForm />
        </div>
      </section>
    </div>
  );
};

export default HomePage;
